import {
  FormControlLabel,
  Typography,
  Stack,
  FormControl,
  Button,
  Box,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect } from 'react';
import UISwitch from '../ui/ui-switch';
import {
  useGetAttachedDealDocumentsQuery,
  useGetContractPartiesCompositionsQuery,
  useGetFactorsSettingsQuery,
  useUpdateFactorsSettingsMutation,
} from '@/app/api';
import UIAutocomplete from '../ui/ui-autocmplete';
import { UIFormLabel } from '../ui/ui-form-label';
import AlertIcon from '../../app/assets/icons/alert.svg?react';
import ConditionsComponent from './components/ConditionsComponent';
import { getFetchEror } from '@/app/helpers';
import { useNotification } from '../contexts/NotificationContext';

interface FormValues {
  isAcceptRequestsFromClientsWithoutLimits: boolean;
  isPercentageOnTopOfAmount: boolean;
  isHeadBankApprovalRequired: boolean;
  idContractPartiesComposition: string;
  mandatoryDealDocument: string[];
  [key: string]: boolean | string | string[];
}

export const FactoringAgreements = () => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const { control, getValues, reset, watch } = useForm<FormValues>({
    defaultValues: {
      isAcceptRequestsFromClientsWithoutLimits: false,
      isPercentageOnTopOfAmount: false,
      isHeadBankApprovalRequired: false,
      idContractPartiesComposition: '',
      mandatoryDealDocument: [],
    },
  });

  const { data: attachedDeals } = useGetAttachedDealDocumentsQuery();

  const { data: contractPartiesCompositions } =
    useGetContractPartiesCompositionsQuery();

  const { data: settings } = useGetFactorsSettingsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const [updateFactorsSettings] = useUpdateFactorsSettingsMutation();

  useEffect(() => {
    if (settings?.data) {
      const settingsData = settings.data;

      const formData: FormValues = {
        isAcceptRequestsFromClientsWithoutLimits:
          settingsData.isAcceptRequestsFromClientsWithoutLimits ?? false,
        isPercentageOnTopOfAmount:
          settingsData.isPercentageOnTopOfAmount ?? false,
        isHeadBankApprovalRequired:
          settingsData.isHeadBankApprovalRequired ?? false,
        idContractPartiesComposition:
          settingsData.idContractPartiesComposition || '',
        mandatoryDealDocument: [],
      };

      if (settingsData.mandatoryDealDocuments) {
        const mandatoryDocIds = settingsData.mandatoryDealDocuments.map(
          (doc) => doc.idAttchedDealDocument
        );
        formData.mandatoryDealDocument = mandatoryDocIds;

        settingsData.mandatoryDealDocuments.forEach((doc) => {
          formData[doc.idAttchedDealDocument] = true;
        });
      }
      reset(formData);
    }
  }, [settings?.data, reset]);

  const handleSaveSettings = useCallback(
    async (values: FormValues) => {
      const checkedDealIds =
        attachedDeals?.data
          ?.filter((deal) => values[deal.id])
          .map((deal) => deal.id) || [];

      if (!checkedDealIds) return;

      try {
        updateFactorsSettings({
          // TODO: change hardcoded values to values from form
          isAcceptRequestsFromClientsWithoutLimits: false,
          isPercentageOnTopOfAmount: values.isPercentageOnTopOfAmount ?? false,
          isHeadBankApprovalRequired: false,
          idContractPartiesComposition: values.idContractPartiesComposition,
          mandatoryDealDocument: checkedDealIds,
        }).unwrap();
        showNotification(
          t('settings.settings_successfully_updated'),
          'success'
        );
      } catch (error) {
        showNotification(getFetchEror(error), 'error');
      }
    },
    [updateFactorsSettings, showNotification, t, attachedDeals?.data]
  );

  const idContractPartiesComposition = watch('idContractPartiesComposition');

  return (
    <Stack spacing={2}>
      <Stack
        sx={{
          background: 'rgba(21, 85, 252, 0.04)',
          p: 2.4,
          borderRadius: '1.2rem',
        }}
      >
        <Stack direction='row' spacing={2}>
          <Stack direction='column' spacing={2} sx={{ width: '50%' }}>
            <Typography fontSize='1.7rem' fontWeight={600}>
              {t('settings.obligatory_documents_for_request')}
            </Typography>

            {attachedDeals?.data?.map((deal) => (
              <Controller
                key={deal.id}
                control={control}
                name={deal.id}
                defaultValue={deal.id === 'invoice' || deal.id === 'contract'}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <UISwitch
                        checked={!!field.value}
                        onChange={field.onChange}
                        disabled={
                          deal.id === 'invoice' || deal.id === 'contract'
                        }
                      />
                    }
                    label={deal.name}
                    sx={{ mb: 2.5 }}
                    slotProps={{
                      typography: {
                        sx: {
                          fontSize: 14,
                          fontWeight: 600,
                          marginLeft: 1.2,
                        },
                      },
                    }}
                  />
                )}
              />
            ))}
          </Stack>

          <Stack direction='column' spacing={2} sx={{ width: '50%' }}>
            <Typography fontSize='1.7rem' fontWeight={600} sx={{ mb: 2 }}>
              {t('settings.additional')}
            </Typography>
            {/* <Controller
              control={control}
              name='isAcceptRequestsFromClientsWithoutLimits'
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <UISwitch
                      checked={!!field.value}
                      onChange={field.onChange}
                    />
                  }
                  label={t(
                    'settings.accept_requests_from_clients_without_limits'
                  )}
                  sx={{ mb: 2.5 }}
                  slotProps={{
                    typography: {
                      sx: {
                        fontSize: 14,
                        fontWeight: 600,
                        marginLeft: 1.2,
                      },
                    },
                  }}
                />
              )}
            /> */}
            <Controller
              control={control}
              name='isPercentageOnTopOfAmount'
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <UISwitch
                      checked={!!field.value}
                      onChange={field.onChange}
                    />
                  }
                  label={t('settings.percentage_on_top_of_amount')}
                  sx={{ mb: 2.5 }}
                  slotProps={{
                    typography: {
                      sx: {
                        fontSize: 14,
                        fontWeight: 600,
                        marginLeft: 1.2,
                      },
                    },
                  }}
                />
              )}
            />
            {/* <Controller
              control={control}
              name='isHeadBankApprovalRequired'
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <UISwitch
                      checked={!!field.value}
                      onChange={field.onChange}
                    />
                  }
                  label={t('settings.head_bank_approval')}
                  sx={{ mb: 2.5 }}
                  slotProps={{
                    typography: {
                      sx: {
                        fontSize: 14,
                        fontWeight: 600,
                        marginLeft: 1.2,
                      },
                    },
                  }}
                />
              )}
            /> */}
          </Stack>
        </Stack>
        <Stack direction='row' alignItems='flex-end' spacing={2} sx={{ mt: 3 }}>
          <Stack direction='column' spacing={2} sx={{ width: '50%' }}>
            <FormControl>
              <UIFormLabel sx={{ color: '#0B1370' }}>
                {t('settings.factoring_contract_signing')}
              </UIFormLabel>
              <Controller
                control={control}
                name='idContractPartiesComposition'
                render={({ field }) => (
                  <UIAutocomplete
                    options={contractPartiesCompositions?.data || []}
                    getOptionLabel={(option) => option.name}
                    value={
                      contractPartiesCompositions?.data?.find(
                        (option) => option.id === field.value
                      ) || null
                    }
                    onChange={(_, selectedOption) => {
                      field.onChange(selectedOption?.id || '');
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        backgroundColor: '#fff',
                      },
                    }}
                  />
                )}
              />
            </FormControl>
          </Stack>
          <Stack
            direction='row'
            alignItems='center'
            spacing={1}
            sx={{
              width: '50%',
              height: '3.6rem',
              background: '#E1E7FA',
              padding: 1,
              borderRadius: '0.8rem',
            }}
          >
            <AlertIcon />
            <Typography fontSize='1.3rem' fontWeight={500}>
              {idContractPartiesComposition === 'twoParty'
                ? t('settings.factoring_contract_signing_description_two')
                : t('settings.factoring_contract_signing_description_three')}
            </Typography>
          </Stack>
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button onClick={() => handleSaveSettings(getValues())}>
            {t('settings.save_changes')}
          </Button>
        </Box>
      </Stack>
      <ConditionsComponent />
    </Stack>
  );
};
